/**
 * NotebookTableControls — the contextual toolbar that floats above the
 * table the cursor is in.
 *
 * It offers the row / column operations of the first version of tables
 * (add + remove rows and columns, delete the table) and nothing else:
 * merging and splitting cells is not part of the product scope, so no
 * such action is offered here.
 *
 * The buttons keep the editor's focus and selection: `mousedown` is
 * prevented so the browser does not move the caret out of the cell before
 * the command runs, and every action goes through the editor chain.
 */

import { useEffect, useRef, useState, type RefObject } from 'react'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Minus, Trash2 } from 'lucide-react'
import type { Editor } from '@tiptap/react'
import { cn } from '@/lib/utils'

export interface NotebookTableControlsProps {
  editor: Editor | null
  /** The scroll container the toolbar is positioned inside. */
  containerRef: RefObject<HTMLDivElement | null>
}

/** Height of the toolbar, used to place it above the table. */
const BAR_HEIGHT = 30

interface TableAction {
  label: string
  icon: typeof ArrowUp
  run: () => void
  danger?: boolean
  /** Draws a separator before the action. */
  separator?: boolean
}

export function NotebookTableControls({ editor, containerRef }: NotebookTableControlsProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const positionRef = useRef<{ top: number; left: number } | null>(null)

  useEffect(() => {
    if (!editor) return
    const container = containerRef.current
    if (!container) return

    const update = () => {
      const element = findTableElement(editor)
      const host = containerRef.current
      if (!element || !host) {
        positionRef.current = null
        setPosition(null)
        return
      }
      const rect = element.getBoundingClientRect()
      const hostRect = host.getBoundingClientRect()
      const next = {
        top: Math.max(0, rect.top - hostRect.top + host.scrollTop - BAR_HEIGHT - 6),
        left: rect.left - hostRect.left + host.scrollLeft,
      }
      const current = positionRef.current
      if (current && current.top === next.top && current.left === next.left) return
      positionRef.current = next
      setPosition(next)
    }

    editor.on('selectionUpdate', update)
    editor.on('transaction', update)
    container.addEventListener('scroll', update)
    update()
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
      container.removeEventListener('scroll', update)
    }
  }, [editor, containerRef])

  if (!editor || !position) return null

  const actions: TableAction[] = [
    { label: 'Row above', icon: ArrowUp, run: () => editor.chain().focus().addRowBefore().run() },
    { label: 'Row below', icon: ArrowDown, run: () => editor.chain().focus().addRowAfter().run() },
    { label: 'Delete row', icon: Minus, run: () => editor.chain().focus().deleteRow().run() },
    {
      label: 'Column left',
      icon: ArrowLeft,
      separator: true,
      run: () => editor.chain().focus().addColumnBefore().run(),
    },
    { label: 'Column right', icon: ArrowRight, run: () => editor.chain().focus().addColumnAfter().run() },
    { label: 'Delete column', icon: Minus, run: () => editor.chain().focus().deleteColumn().run() },
    {
      label: 'Delete table',
      icon: Trash2,
      separator: true,
      danger: true,
      run: () => editor.chain().focus().deleteTable().run(),
    },
  ]

  return (
    <div
      role="toolbar"
      aria-label="Table actions"
      data-testid="table-controls"
      className="absolute z-20 flex max-w-[calc(100%-4rem)] items-center gap-0.5 overflow-x-auto whitespace-nowrap rounded-lg border border-surface-200 bg-white px-1 py-1 shadow-lg shadow-black/5 dark:border-surface-700 dark:bg-surface-800"
      style={{ top: position.top, left: position.left }}
    >
      {actions.map((action) => {
        const Icon = action.icon
        return (
          <span key={action.label} className="inline-flex items-center">
            {action.separator && (
              <span
                aria-hidden="true"
                className="mx-0.5 h-4 w-px bg-surface-200 dark:bg-surface-700"
              />
            )}
            <button
              type="button"
              aria-label={action.label}
              title={action.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={action.run}
              className={cn(
                'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors',
                'text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-700',
                action.danger && 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30',
              )}
            >
              <Icon size={13} aria-hidden="true" />
            </button>
          </span>
        )
      })}
    </div>
  )
}

/** The DOM element of the table the cursor is in, if any. */
function findTableElement(editor: Editor): HTMLElement | null {
  const { $from } = editor.state.selection
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name !== 'table') continue
    const dom = editor.view.nodeDOM($from.before(depth))
    return dom instanceof HTMLElement ? dom : null
  }
  return null
}
